import {clx, TextMedium} from '@hm/fundamental'
import React, {useState, useEffect} from 'react'

import {DataScopeInput, LookupDataScopeInput} from '../../../modules/DataScopeConfiguration'
import {GroupedRoleReducerAction} from '../../../modules/GroupedRoleAssignmentReducer'
import {Permission} from '../../../modules/LoggedInUser'
import {GroupedRoleAssignment} from '../../../modules/ManageUsers.selectors'

import {LookupDataScope} from './LookupDataScope'

type PayerOrCustomerSelectionDataScopeProps = {
  dispatchRoleStateUpdate: React.Dispatch<GroupedRoleReducerAction>
  groupedRoleAssignment: GroupedRoleAssignment
  customerAdminData?: Record<string, string | undefined>
  setShowCustomerOrPayerDropdown: (show: boolean) => void
  showCustomerOrPayerDropdown: boolean
  loggedInUserPermissions?: Permission[] | null
  payerConfiguration: DataScopeInput
  customerConfiguration: DataScopeInput
  isRequired: boolean
  multiSelect: boolean
  errorMessage: string | null
  roleDataScopeNames?: Set<string>
}

export const PayerOrCustomerSelectionDataScope: React.FC<
  PayerOrCustomerSelectionDataScopeProps
> = ({
  dispatchRoleStateUpdate,
  groupedRoleAssignment,
  customerAdminData,
  setShowCustomerOrPayerDropdown,
  showCustomerOrPayerDropdown,
  loggedInUserPermissions,
  payerConfiguration,
  customerConfiguration,
  isRequired,
  multiSelect,
  errorMessage,
  roleDataScopeNames
}) => {
  const hasPayerIds =
    groupedRoleAssignment.variantDataScopes.payerIds &&
    groupedRoleAssignment.variantDataScopes.payerIds.length > 0
  const hasCustomerScopes =
    groupedRoleAssignment.variantDataScopes.customersScopes &&
    groupedRoleAssignment.variantDataScopes.customersScopes.length > 0

  const getInitialSelection = (): 'payerIds' | 'customerIds' => {
    if (hasPayerIds && !hasCustomerScopes) return 'payerIds'
    if (hasCustomerScopes && !hasPayerIds) return 'customerIds'
    return 'customerIds'
  }

  const [selectedType, setSelectedType] = useState<'payerIds' | 'customerIds'>(
    getInitialSelection()
  )

  useEffect(() => {
    setSelectedType(getInitialSelection())
  }, [hasPayerIds, hasCustomerScopes])

  const handleSelectionChange = (value: string) => {
    const newSelection = value as 'payerIds' | 'customerIds'
    setSelectedType(newSelection)
  }

  const currentDataScopeName = selectedType
  const currentConfiguration =
    selectedType === 'payerIds' ? payerConfiguration : customerConfiguration
  const fieldName = `commonDataScopes.${currentDataScopeName}`

  useEffect(() => {
    handleSelectionChange('customerIds')
  }, [])

  const options = [
    {label: 'Customer', value: 'customerIds'},
    {label: 'Payer', value: 'payerIds'}
  ]

  return (
    <div className="flex gap-2 w-full h-12 max-w-[600px]">
      <LookupDataScope
        key={fieldName}
        errorMessage={errorMessage || ''}
        configuration={currentConfiguration as LookupDataScopeInput}
        groupedRoleAssignment={groupedRoleAssignment}
        dataScopeName={currentDataScopeName}
        isRequired={isRequired}
        dispatchRoleStateUpdate={dispatchRoleStateUpdate}
        multiSelect={multiSelect}
        customerAdminData={customerAdminData}
        setShowCustomerOrPayerDropdown={setShowCustomerOrPayerDropdown}
        showCustomerOrPayerDropdown={showCustomerOrPayerDropdown}
        loggedInUserPermissions={loggedInUserPermissions}
        roleDataScopeNames={roleDataScopeNames}
      />
      <div
        className="flex rounded-[10px] p-1 h-full bg-button_background_selected"
        data-cy="payer-or-customer-selection"
      >
        {options.map((option) => (
          <button
            data-cy={`selection-${option.value}`}
            name={option.value.toString()}
            key={option.value}
            type="button"
            className={clx(
              'px-3 py-2 rounded-md h-full',
              selectedType === option.value
                ? 'bg-primary text-foreground_inverted'
                : 'bg-inherit text-foreground'
            )}
            onClick={() => setSelectedType(option.value as 'payerIds' | 'customerIds')}
          >
            <TextMedium>{option.label}</TextMedium>
          </button>
        ))}
      </div>
    </div>
  )
}
